/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useMediaQuery } from '@material-ui/core';

import Message from '@material-ui/icons/Message';
import { useTranslation } from 'react-i18next';
import useSummary from './useSummary';
import BasicFilingCard from './summaryCards/BasicFilingCard';
import DeclarationsCard from './summaryCards/DeclarationsCard';
import BioCard from './summaryCards/BioCard';
import PartiesCard from './summaryCards/PartiesCard';
import AttachmentsCard from './summaryCards/attachments/AttachmentsCard';
import FeesCard from './summaryCards/fees/FeesCard';
import MediumSummary from './MediumSummary';
import SmallSummary from './SmallSummary';
import LargeSummary from './LargeSummary';
import AdditionalNotesCard from './summaryCards/AdditionalNotesCard';
import { SubmissionSectionType } from '../common/SummaryCard/CardBody/SubmissionSectionType';
import GenericCard from './summaryCards/GenericCard';
import { uiSchemaProperties } from '../../shared/enums/uiSchemaProperties';

/**
 * Based on the screen size selects which Summary component will display the cards for the main submission component.
 * @param submission - submission object to generate summary for
 * @param isSummary - true when used with the Send Confirmation screen
 * @param uiSchema - json ui schema
 * @param readOnly - read only submission mode 'true' makes the cards read only
 * @param onSectionChange
 * @param hideLinkTo - hides the linkTo button
 * @returns {JSX.Element}
 * @constructor
 */
export default function SummarySelector({
  submission,
  isSummary,
  uiSchema,
  readOnly,
  onSectionChange,
  hideLinkTo = false,
  jsonSchema,
}) {
  const { getSummarySectionEntries } = useSummary(submission, uiSchema, hideLinkTo, jsonSchema);
  const { t } = useTranslation();

  const receiptSection = uiSchema[SubmissionSectionType.RECEIPT];
  const receiptEntries = getSummarySectionEntries(SubmissionSectionType.RECEIPT);
  const receiptCard =
    receiptEntries?.length > 0 ? (
      <GenericCard
        configurableIcon={receiptSection?.[uiSchemaProperties.UI_ICON] || 'send'}
        cardTitle={receiptSection?.[uiSchemaProperties.UI_TITLE] || t('messages.submission.send.confirm')}
        entries={receiptEntries}
        sectionName="submission_receiptCard"
        isSummary={isSummary}
        readOnly={readOnly}
      />
    ) : (
      <></>
    );

  const basicFilingInfoSection = uiSchema[SubmissionSectionType.BASIC_FILING];
  const basicFilingCard = basicFilingInfoSection && !basicFilingInfoSection.disabledSummaryCard && (
    <BasicFilingCard
      fillingEntries={getSummarySectionEntries(SubmissionSectionType.BASIC_FILING)}
      isSummary={isSummary}
      readOnly={readOnly}
      cardTitle={basicFilingInfoSection?.[uiSchemaProperties.UI_TITLE]}
    />
  );

  const requestInformationSection = uiSchema[SubmissionSectionType.REQUEST_INFO];
  const requestInfoCard = requestInformationSection && !requestInformationSection.disabledSummaryCard && (
    <GenericCard
      icon={Message}
      cardTitle={requestInformationSection?.[uiSchemaProperties.UI_TITLE]}
      entries={getSummarySectionEntries(SubmissionSectionType.REQUEST_INFO)}
      sectionName="submission_requestInformation"
      isSummary={isSummary}
      readOnly={readOnly}
    />
  );

  const seqlBioSection = uiSchema[SubmissionSectionType.SEQLBIO];
  const bioCard = seqlBioSection && !seqlBioSection.disabledSummaryCard && (
    <BioCard
      bioEntries={getSummarySectionEntries(SubmissionSectionType.SEQLBIO)}
      isSummary={isSummary}
      readOnly={readOnly}
      submission={submission}
      onSectionChange={onSectionChange}
      cardTitle={seqlBioSection?.[uiSchemaProperties.UI_TITLE]}
    />
  );

  const attachmentsSection = uiSchema[SubmissionSectionType.ATTACHMENTS];
  const attachmentsCard = attachmentsSection && !attachmentsSection.disabledSummaryCard && (
    <AttachmentsCard
      attachmentEntries={getSummarySectionEntries(SubmissionSectionType.ATTACHMENTS)}
      isSummary={isSummary}
      readOnly={readOnly}
      cardTitle={attachmentsSection?.[uiSchemaProperties.UI_TITLE]}
    />
  );

  const partiesSection = uiSchema[SubmissionSectionType.PARTIES];
  const partiesCard = partiesSection && !partiesSection.disabledSummaryCard && (
    <PartiesCard
      partiesEntries={getSummarySectionEntries(SubmissionSectionType.PARTIES)}
      isSummary={isSummary}
      uiSchema={uiSchema}
      readOnly={readOnly}
      submission={submission}
      onSectionChange={onSectionChange}
      cardTitle={partiesSection?.[uiSchemaProperties.UI_TITLE]}
    />
  );

  const declarationsSection = uiSchema[SubmissionSectionType.DECLARATIONS];
  const declarationsCard = declarationsSection && !declarationsSection.disabledSummaryCard && (
    <DeclarationsCard
      declarationEntries={getSummarySectionEntries(SubmissionSectionType.DECLARATIONS)}
      isSummary={isSummary}
      readOnly={readOnly}
      cardTitle={declarationsSection?.[uiSchemaProperties.UI_TITLE]}
    />
  );

  const feesSection = uiSchema[SubmissionSectionType.FEES];
  const feesCard = feesSection && !feesSection.disabledSummaryCard && (
    <FeesCard
      feeEntries={getSummarySectionEntries(SubmissionSectionType.FEES)}
      isSummary={isSummary}
      readOnly={readOnly}
      icon={feesSection?.[uiSchemaProperties.UI_ICON]}
      cardTitle={feesSection?.[uiSchemaProperties.UI_TITLE]}
    />
  );

  const additionalNotesSection = uiSchema[SubmissionSectionType.ADDITIONAL_NOTES];
  const additionalNotesCard = additionalNotesSection && !additionalNotesSection.disabledSummaryCard && (
    <AdditionalNotesCard
      additionalNotesEntries={getSummarySectionEntries(SubmissionSectionType.ADDITIONAL_NOTES)}
      isSummary={isSummary}
      readOnly={readOnly}
      cardTitle={additionalNotesSection?.[uiSchemaProperties.UI_TITLE]}
    />
  );

  const hasSmallLayout = useMediaQuery(theme => theme.breakpoints.down('md'));
  const hasLargeLayout = useMediaQuery(theme => theme.breakpoints.up('xl3'));
  const hasMediumLayout = !hasSmallLayout && !hasLargeLayout;
  const orderedBodyCards = [
    basicFilingCard,
    requestInfoCard,
    partiesCard,
    declarationsCard,
    bioCard,
    attachmentsCard,
    feesCard,
    additionalNotesCard,
  ]
    .filter(item => item != null)
    .map((item, index) => <div key={index}>{item}</div>);

  if (hasSmallLayout) {
    return <SmallSummary receiptCard={receiptCard} orderedBodyCards={orderedBodyCards} submission={submission} />;
  }

  if (hasMediumLayout) {
    return <MediumSummary receiptCard={receiptCard} orderedBodyCards={orderedBodyCards} submission={submission} />;
  }

  if (hasLargeLayout) {
    return <LargeSummary submission={submission} receiptCard={receiptCard} orderedBodyCards={orderedBodyCards} />;
  }
}
